import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { setSentryUser, clearSentryUser } from './sentry'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false)

  useEffect(() => {
    // Detect recovery/invite links from URL hash BEFORE getSession, since
    // Supabase auto-exchanges the token on load and clears the fragment.
    const hash = window.location.hash
    if (hash.includes('type=recovery') || hash.includes('type=invite')) {
      setNeedsPasswordSetup(true)
    }

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session)
        setUser(session?.user ?? null)
        if (session?.user) {
          fetchProfile(session.user.id)
        } else {
          setLoading(false)
        }
      })
      .catch(err => {
        console.error('getSession failed:', err)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (event === 'PASSWORD_RECOVERY') {
        setNeedsPasswordSetup(true)
      }
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setProfile(null)
        setNeedsPasswordSetup(false)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId) {
    try {
      // First try matching by user_id. Order admin > member > foster so a
      // user who happens to have multiple profile rows (e.g. an import dup)
      // resolves to the highest-privilege one and the app keeps working
      // while an admin cleans the dup up. .single() throws on >1 row, so
      // we limit(1) instead and grab the first.
      const byUser = await supabase
        .from('user_profiles')
        .select('*, organizations(*)')
        .eq('user_id', userId)
        .order('role', { ascending: true }) // admin < foster < member alphabetically — admin wins
        .limit(1)
      let data = byUser.data?.[0] || null
      let error = byUser.error

      // If no match, try matching by email and link the user_id (first login)
      if (error || !data) {
        const { data: { user: authUser } } = await supabase.auth.getUser()
        if (authUser?.email) {
          // maybeSingle so duplicate or zero rows don't throw
          const { data: emailMatch } = await supabase
            .from('user_profiles')
            .select('*, organizations(*)')
            .eq('email', authUser.email)
            .is('user_id', null)
            .limit(1)
            .maybeSingle()

          if (emailMatch) {
            await supabase
              .from('user_profiles')
              .update({ user_id: userId })
              .eq('id', emailMatch.id)
            data = { ...emailMatch, user_id: userId }
          }
        }
      }

      if (data) {
        // Block deactivated accounts: sign them straight back out so they
        // can't progress past the auth check. Flag the sessionStorage key
        // before signOut so ProtectedRoute can route them to /deactivated
        // instead of silently landing on /login.
        if (data.deactivated_at) {
          try { sessionStorage.setItem('placed:deactivated', '1') } catch { /* noop */ }
          await supabase.auth.signOut()
          setProfile(null)
          return
        }
        // BIG-60 — admin-direct-create accounts may carry a flag forcing
        // the user through SetPasswordPage on first login.
        if (data.must_change_password) setNeedsPasswordSetup(true)
        setProfile(data)
        // Tag Sentry events with hashed user_id + role + org_id so the
        // dashboard can filter by who hit what (BIG-74 D4).
        setSentryUser({ userId: userId, role: data.role, orgId: data.org_id })
      } else {
        console.error('No profile found')
        setProfile(null)
      }
    } catch (err) {
      // Network errors must not leave the app stuck on PageLoading.
      console.error('fetchProfile failed:', err)
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }

  async function signInWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    })
    if (error) console.error('Google sign-in error:', error)
  }

  async function signInWithEmail(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signUp(email, password) {
    const { error } = await supabase.auth.signUp({ email, password })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setProfile(null)
    clearSentryUser()
  }

  const value = {
    session,
    user,
    profile,
    loading,
    needsPasswordSetup,
    clearPasswordSetup: () => setNeedsPasswordSetup(false),
    signInWithGoogle,
    signInWithEmail,
    signUp,
    signOut,
    refreshProfile: () => user && fetchProfile(user.id),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
